import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal } from '@/lib/swal'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { shiftService } from '@/services/shiftService'
import type { EmpShiftsVm } from '@/api/types'

const SHIFT_TYPES = [
  { value: 'Morning', label: 'Morning' },
  { value: 'Evening', label: 'Evening' },
  { value: 'Night', label: 'Night' },
  { value: 'General', label: 'General' },
]

export const ShiftDetail: React.FC = () => {
  const { t: _t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditMode = id !== 'new' && id != null

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [shift, setShift] = useState<EmpShiftsVm>({
    id: 0,
    branchId: branchId ?? 0,
    shiftType: '',
    startTime: '',
    endTime: '',
    description: '',
  })

  useEffect(() => {
    if (!branchId) return
    setShift((prev) => ({ ...prev, branchId }))
  }, [branchId])

  useEffect(() => {
    if (!isEditMode || !id || !branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await shiftService.getById(Number(id), branchId)
        setShift({
          id: data.id,
          branchId: data.branchId ?? branchId,
          shiftType: data.name ?? data.shiftType ?? '',
          startTime: (data.shiftStartTime ?? data.startTime) ? toInputTime(String(data.shiftStartTime ?? data.startTime ?? '')) : '',
          endTime: (data.shiftEndTime ?? data.endTime) ? toInputTime(String(data.shiftEndTime ?? data.endTime ?? '')) : '',
          description: data.shiftDescription ?? data.description ?? '',
        })
      } catch (e: any) {
        console.error(e)
        setShift((prev) => ({ ...prev, id: Number(id) }))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditMode, id, branchId])

  function toInputTime(val: string): string {
    if (!val) return ''
    if (/^\d{2}:\d{2}$/.test(val)) return val
    try {
      const d = new Date(val)
      const h = d.getHours()
      const m = d.getMinutes()
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    } catch {
      return val
    }
  }

  /** Convert "HH:mm" to full ISO datetime (today's date + time) for API */
  function timeToISO(timeStr: string | undefined): string {
    if (!timeStr || !/^\d{1,2}:\d{2}$/.test(timeStr)) return new Date().toISOString()
    const [h, m] = timeStr.split(':').map(Number)
    const d = new Date()
    d.setHours(h, m, 0, 0)
    return d.toISOString()
  }

  const handleChange = (field: keyof EmpShiftsVm, value: string | number | undefined) => {
    setShift((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError('Please select a branch')
      return
    }
    if (!shift.description?.trim()) {
      await showValidationError('Description is required')
      return
    }
    try {
      setSaving(true)
      showLoading(isEditMode ? 'Updating...' : 'Saving...', 'Please wait')
      const payload = {
        id: shift.id ?? 0,
        name: shift.shiftType ?? shift.description?.trim() ?? '',
        shiftStartTime: timeToISO(shift.startTime),
        shiftEndTime: timeToISO(shift.endTime),
        shiftDescription: shift.description?.trim() ?? '',
        branchId,
      }
      console.log('🔵 [ShiftDetail] Save payload (API structure):', JSON.stringify(payload, null, 2))
      if (isEditMode && shift.id) {
        await shiftService.update(payload)
      } else {
        await shiftService.create(payload)
      }
      closeSwal()
      await showSaveSuccess(isEditMode, isEditMode ? 'Shift updated successfully' : 'Shift created successfully')
      navigate('/shifts')
    } catch (error: any) {
      closeSwal()
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch.</p>
            <Button className="mt-4" variant="outline" onClick={() => navigate('/shifts')}>
              Return
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Shift</h1>
          <p className="text-muted-foreground">Shift Detail</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Shifts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Shift Type *</Label>
                  <Select
                    value={shift.shiftType || ''}
                    onValueChange={(v) => handleChange('shiftType', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select shift type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SHIFT_TYPES.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={shift.startTime || ''}
                    onChange={(e) => handleChange('startTime', e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={shift.endTime || ''}
                    onChange={(e) => handleChange('endTime', e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Discription *</Label>
                <Textarea
                  placeholder="Required example textarea"
                  value={shift.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  disabled={saving}
                  rows={4}
                  className="resize-y"
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => navigate('/shifts')} disabled={saving}>
              Return
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
